import React from "react";

export default function Gdpr() {
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-3xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">GDPR Compliance Page</h1>
        <p className="text-sm text-gray-500 mb-6">
          <strong>Last Updated:</strong> 4 April 2026<br />
          <strong>Trading Name:</strong> VSENDS<br />
          <strong>Legal Entity:</strong> V CAN RESOURCES LIMITED<br />
          <strong>Company Number:</strong> 13759348<br />
          <strong>Registered Office:</strong> 32 Abbey Drive, Dartford, England, DA2 7WP<br />
          <strong>Support Email:</strong> support@vsends.com<br />
          <strong>Compliance Email:</strong> vg@vcanresources.com
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">1. Our Commitment to GDPR</h2>
        <p className="text-gray-700 mb-4">
          V CAN RESOURCES LIMITED ("we", "us", "our") is fully committed to complying with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. This page outlines our approach to GDPR compliance and the measures we have implemented to protect the personal data of individuals in the UK and the European Economic Area (EEA).
        </p>
        <p className="text-gray-700 mb-4">
          We take our data protection responsibilities seriously and have invested in the people, processes, and technology necessary to ensure compliance.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">2. Data Controller and Processor Roles</h2>
        <p className="text-gray-700 mb-4">
          Under the UK GDPR, organisations can act as either data controllers or data processors:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>As a data controller:</strong> We determine the purposes and means of processing personal data for our own business operations, such as managing user Accounts, processing payments, and providing customer support.</li>
          <li><strong>As a data processor:</strong> We process personal data on behalf of our users (the controllers) when they use the Service to store and send communications to their contacts.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Our Data Processing Agreement (DPA) sets out the full details of our obligations as a processor and is automatically incorporated into our Terms of Service.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">3. Lawful Basis for Processing</h2>
        <p className="text-gray-700 mb-4">
          We process personal data only where we have a lawful basis to do so under Article 6 of the UK GDPR. Our lawful bases include:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Contract:</strong> Processing is necessary to perform our contract with you (e.g., providing the Service).</li>
          <li><strong>Legitimate interests:</strong> Processing is necessary for our legitimate business interests, such as improving the Service, preventing fraud, and ensuring security.</li>
          <li><strong>Consent:</strong> You have given clear consent for us to process your personal data for a specific purpose.</li>
          <li><strong>Legal obligation:</strong> Processing is necessary for us to comply with the law.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">4. Data Subject Rights</h2>
        <p className="text-gray-700 mb-4">
          Under the UK GDPR, individuals have the following rights in relation to their personal data:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Right to be informed:</strong> You have the right to be informed about the collection and use of your personal data.</li>
          <li><strong>Right of access:</strong> You can request a copy of the personal data we hold about you.</li>
          <li><strong>Right to rectification:</strong> You can request correction of inaccurate or incomplete personal data.</li>
          <li><strong>Right to erasure:</strong> You can request deletion of your personal data in certain circumstances.</li>
          <li><strong>Right to restrict processing:</strong> You can request that we limit the way we use your personal data.</li>
          <li><strong>Right to data portability:</strong> You can request a machine-readable copy of your personal data.</li>
          <li><strong>Right to object:</strong> You can object to processing based on legitimate interests or direct marketing.</li>
          <li><strong>Rights related to automated decision-making:</strong> You have rights in relation to automated decision-making and profiling.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          To exercise any of these rights, please contact us at vg@vcanresources.com. We will respond within 30 calendar days.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">5. Data Protection by Design and Default</h2>
        <p className="text-gray-700 mb-4">
          We have implemented data protection by design and by default across the Service. This means:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Data protection considerations are integrated into the development of all new features.</li>
          <li>By default, only personal data that is necessary for each specific purpose is processed.</li>
          <li>Personal data is not made accessible to an indefinite number of individuals without the individual's action.</li>
          <li>Privacy settings are configured to the most protective options by default.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">6. Data Security Measures</h2>
        <p className="text-gray-700 mb-4">
          We implement appropriate technical and organisational measures to ensure a level of security appropriate to the risk, including:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Encryption of personal data in transit (TLS 1.2+) and at rest (AES-256).</li>
          <li>Regular security assessments, vulnerability scanning, and penetration testing.</li>
          <li>Access controls, including role-based access and multi-factor authentication.</li>
          <li>Employee training on data protection and information security.</li>
          <li>Incident response and breach notification procedures.</li>
          <li>Regular backup and disaster recovery testing.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">7. Data Breach Procedures</h2>
        <p className="text-gray-700 mb-4">
          In the event of a personal data breach, we have procedures in place to:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Detect, investigate, and contain the breach.</li>
          <li>Assess the risk to individuals' rights and freedoms.</li>
          <li>Notify the Information Commissioner's Office (ICO) within 72 hours where the breach poses a risk.</li>
          <li>Notify affected individuals without undue delay where the breach poses a high risk.</li>
          <li>Document all breaches, including the facts, effects, and remedial actions taken.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">8. International Data Transfers</h2>
        <p className="text-gray-700 mb-4">
          Where we transfer personal data outside the UK, we ensure appropriate safeguards are in place in accordance with Chapter V of the UK GDPR. These safeguards include:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Transfers to countries with an adequacy decision from the UK government.</li>
          <li>UK International Data Transfer Agreements or Addendums approved by the ICO.</li>
          <li>Binding Corporate Rules where applicable.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          We maintain a record of all international data transfers and the safeguards applied. You may request this information by contacting vg@vcanresources.com.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">9. Data Protection Officer</h2>
        <p className="text-gray-700 mb-4">
          We have appointed a Data Protection Officer (DPO) who is responsible for overseeing our data protection strategy and ensuring compliance with the UK GDPR. Our DPO can be contacted at:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Email:</strong> vg@vcanresources.com</li>
          <li><strong>Post:</strong> Data Protection Officer, V CAN RESOURCES LIMITED, 32 Abbey Drive, Dartford, England, DA2 7WP</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Our DPO operates independently and reports directly to senior management on data protection matters.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">10. Records of Processing Activities</h2>
        <p className="text-gray-700 mb-4">
          We maintain comprehensive records of all processing activities carried out under our responsibility as both controller and processor. These records include:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>The purposes of the processing.</li>
          <li>The categories of data subjects and personal data.</li>
          <li>The categories of recipients to whom data has been or will be disclosed.</li>
          <li>Details of international data transfers and safeguards.</li>
          <li>Retention periods for each category of data.</li>
          <li>A general description of technical and organisational security measures.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">11. Data Protection Impact Assessments</h2>
        <p className="text-gray-700 mb-4">
          We conduct Data Protection Impact Assessments (DPIAs) for processing activities that are likely to result in a high risk to individuals' rights and freedoms. DPIAs help us identify and minimise data protection risks before they materialise.
        </p>
        <p className="text-gray-700 mb-4">
          We carry out DPIAs when:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Introducing new technologies or processing methods.</li>
          <li>Processing personal data on a large scale.</li>
          <li>Systematically monitoring individuals on a large scale.</li>
          <li>Processing special category data or criminal conviction data.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">12. Supervisory Authority</h2>
        <p className="text-gray-700 mb-4">
          Our lead supervisory authority is the Information Commissioner's Office (ICO), the UK's independent authority set up to uphold information rights. You have the right to lodge a complaint with the ICO if you believe your data protection rights have been violated.
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Website:</strong> www.ico.org.uk</li>
          <li><strong>Helpline:</strong> 0303 123 1113</li>
          <li><strong>Address:</strong> Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF</li>
        </ul>
        <p className="text-gray-700 mb-4">
          We encourage you to contact us first so we can attempt to resolve your concern before you escalate it to the ICO.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">13. Ongoing Compliance</h2>
        <p className="text-gray-700 mb-4">
          GDPR compliance is an ongoing commitment. We regularly review and update our data protection practices to ensure continued compliance with evolving regulations and best practices. This includes:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Annual reviews of our data protection policies and procedures.</li>
          <li>Regular training for all employees on data protection obligations.</li>
          <li>Periodic audits of our processing activities and security measures.</li>
          <li>Monitoring of regulatory developments and guidance from the ICO.</li>
          <li>Engagement with external data protection experts and consultants.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          For any questions about our GDPR compliance, please contact our Data Protection Officer at vg@vcanresources.com.
        </p>
      </main>
    </div>
  );
}
